import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import './DashboardLayout.css';
import { useUser } from '../../context/UserContext';
import { useFilter } from '../../context/FilterContext';
import { useI18n } from '../../i18n/I18nContext';
import ChatBotGemini from '../ChatBotGemini';
import { getPendingOffers, saveOffer } from '../../services/messageService';
import { getIncidents } from '../../services/transporteurService';
import Logo from '../common/Logo';
import useWebSocket from '../../hooks/useWebSocket';
import { toast } from 'react-toastify';

const DashboardLayout = ({ children, hideSidebar = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, locale, setLocale } = useI18n();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false); // for mobile
  const { userType, logout, userId, token } = useUser();
  const { selectedTransporteurId, setSelectedTransporteurId, subcontractors, isResponsable } = useFilter();
  const isTransporteurLike = userType === 'transporteur' || userType === 'responsable_transporteur';
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [openSubmenus, setOpenSubmenus] = useState([]);
  const [chatBotOpen, setChatBotOpen] = useState(false);
  const [offerCount, setOfferCount] = useState(0);
  const [notificationsDropdownOpen, setNotificationsDropdownOpen] = useState(false);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [notifications, setNotifications] = useState({ offers: [], incidents: [] });
  // Frontend-only read/unread state stored in localStorage
  const STORAGE_KEY = 'bfret_read_notifications';
  const [readIds, setReadIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(STORAGE_KEY)) || []); }
    catch { return new Set(); }
  });

  let navigationItems = [
    {
      path: '/dashboard',
      icon: 'bi bi-speedometer2',
      label: t('common.dashboard')
    },
    ...(isTransporteurLike ? [
      {
        path: '/agent-center',
        icon: 'bi bi-robot',
        label: t('nav.agentCenter'),
        roles: ['transporteur', 'responsable_transporteur']
      }
    ] : []),

    // Missions: simple link for expediteur, dropdown for transporteur
    (
      userType === 'expediteur'
        ? {
          path: '/expediteur/missions',
          icon: 'bi bi-truck',
          label: t('common.missions'),
          roles: ['expediteur']
        }
        : {
          icon: 'bi bi-truck',
          label: t('common.missions'),
          isDropdown: true,
          subItems: [
            {
              path: '/missions',
              icon: 'bi bi-list-ul',
              label: t('common.missionList'),
              roles: ['transporteur', 'responsable_transporteur']
            },
            {
              path: '/missions/recurring',
              icon: 'bi bi-calendar-event',
              label: t('common.recurringMissions') || 'Missions Récurrentes',
              roles: ['transporteur', 'responsable_transporteur']
            },
            {
              path: '/missions/settings',
              icon: 'bi bi-gear',
              label: t('common.missionSettings'),
              roles: ['transporteur', 'responsable_transporteur']
            },
            {
              path: '/missions/assign',
              icon: 'bi bi-person-plus',
              label: t('common.assignMission'),
              roles: ['transporteur', 'responsable_transporteur']
            },
            {
              path: '/subcontracted-missions',
              icon: 'bi bi-share',
              label: t('common.subcontractedMissions'),
              roles: ['transporteur', 'responsable_transporteur']
            }
          ]
        }
    ),
    ...(userType === 'expediteur'
      ? [{
        path: '/expediteur/transporteurs',
        icon: 'bi bi-building-add',
        label: t('nav.transporteurs'),
        roles: ['expediteur']
      }]
      : []),
    ...(isTransporteurLike ? [
      {
        path: '/chauffeurs',
        icon: 'bi bi-people',
        label: t('common.chauffeurs'),
        roles: ['transporteur', 'responsable_transporteur']
      },
      {
        path: '/vehicules',
        icon: 'bi bi-truck',
        label: t('common.vehicules'),
        roles: ['transporteur', 'responsable_transporteur']
      },
      {
        path: '/remorques',
        icon: 'bi bi-truck-flatbed',
        label: t('common.remorques'),
        roles: ['transporteur', 'responsable_transporteur']
      },
      {
        icon: 'bi bi-briefcase',
        label: t('common.clientMissions'),
        isDropdown: true,
        subItems: [
          {
            path: '/client',
            icon: 'bi bi-list-stars',
            label: t('common.clientOffers'),
            roles: ['transporteur', 'responsable_transporteur']
          },
          {
            path: '/clients',
            icon: 'bi bi-people-fill',
            label: t('common.clients'),
            roles: ['transporteur', 'responsable_transporteur']
          }
        ]
      },
      {
        path: '/billing',
        icon: 'bi bi-file-earmark-check',
        label: t('billing.title'),
        roles: ['transporteur', 'responsable_transporteur']
      },
      {
        path: '/gps-history',
        icon: 'bi bi-geo-alt',
        label: t('common.gpsHistory'),
        roles: ['transporteur', 'responsable_transporteur']
      }
    ] : []),
    ...(isTransporteurLike ? [
      {
        path: '/messagerie',
        icon: 'bi bi-chat-dots',
        label: t('common.messagerieInterne'),
        roles: ['transporteur', 'responsable_transporteur']
      }
    ] : []),
    ...(userType === 'responsable_transporteur' ? [
      {
        path: '/responsable/transporteurs',
        icon: 'bi bi-people-fill',
        label: t('common.exploitants'),
        roles: ['responsable_transporteur']
      }
    ] : []),
    ...(isTransporteurLike ? [
      {
        icon: 'bi bi-bar-chart-line',
        label: t('nav.reports'),
        isDropdown: true,
        subItems: [
          {
            path: '/reports/general',
            icon: 'bi bi-graph-up',
            label: t('nav.reportsGeneral'),
            roles: ['transporteur', 'responsable_transporteur']
          },
          {
            path: '/reports/subcontracting',
            icon: 'bi bi-diagram-3',
            label: t('nav.subcontracting'),
            roles: ['transporteur', 'responsable_transporteur']
          },
          {
            path: '/reports/drivers',
            icon: 'bi bi-people',
            label: t('nav.drivers'),
            roles: ['transporteur', 'responsable_transporteur']
          },
          {
            path: '/reports/vehicles',
            icon: 'bi bi-truck',
            label: t('nav.vehicles'),
            roles: ['transporteur', 'responsable_transporteur']
          },

          {
            path: '/reports/billing',
            icon: 'bi bi-receipt',
            label: t('nav.billing'),
            roles: ['transporteur', 'responsable_transporteur']
          }
        ]
      }
    ] : []),
    ...(isTransporteurLike ? [
      {
        path: '/subcontractors',
        icon: 'bi bi-people-fill',
        label: t('nav.mySubcontractors'),
        roles: ['transporteur', 'responsable_transporteur']
      },
      {
        path: '/subcontract',
        icon: 'bi bi-share',
        label: t('common.subcontract'),
        roles: ['transporteur', 'responsable_transporteur']
      }
    ] : []),
    {
      path: '/profile',
      icon: 'bi bi-person',
      label: t('common.profile'),
    }
  ];

  // Admin: admin pages
  if (userType === 'admin') {
    navigationItems = [
      {
        path: '/admin',
        icon: 'bi bi-speedometer2',
        label: t('common.dashboard')
      },
      {
        path: '/admin/users',
        icon: 'bi bi-people',
        label: t('admin.userList.title')
      },
      {
        path: '/admin/users/create',
        icon: 'bi bi-person-plus',
        label: t('admin.createUserAccount')
      },
      {
        path: '/admin/transporteurs/limits',
        icon: 'bi bi-sliders',
        label: t('admin.transporteurLimits')
      }
    ];
  }

  const filteredNavItems = navigationItems.filter(item =>
    !item.roles || item.roles.includes(userType)
  );

  const currentPage = filteredNavItems.find(item => item.path === location.pathname);

  // Handle sidebar toggle for mobile
  const handleSidebarToggle = () => setSidebarOpen(!sidebarOpen);
  const handleOverlayClick = () => setSidebarOpen(false);
  const handleDropdownToggle = () => setDropdownOpen(!dropdownOpen);
  const handleDropdownClose = () => setDropdownOpen(false);

  // Close mobile sidebar only
  const handleNavClick = () => {
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  };

  const handleSubmenuToggle = (label) => {
    setOpenSubmenus(prev =>
      prev.includes(label)
        ? prev.filter(l => l !== label)
        : [...prev, label]
    );
  };

  // Add a handler to logout and navigate
  const handleLogout = () => {
    logout();
    // Do NOT navigate here; let ProtectedRoute handle it
  };

  // Mark a single notification as read
  const markAsRead = (id) => {
    setReadIds(prev => {
      const next = new Set(prev);
      next.add(String(id));
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
      return next;
    });
  };

  // Mark all current notifications as read
  const markAllRead = () => {
    const allIds = [
      ...notifications.incidents.map(i => String(i.id)),
      ...notifications.offers.map(o => String('offer_' + o.id))
    ];
    setReadIds(prev => {
      const next = new Set([...prev, ...allIds]);
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
      return next;
    });
  };

  // Unread count
  const unreadCount = [
    ...notifications.incidents.filter(i => !readIds.has(String(i.id))),
    ...notifications.offers.filter(o => !readIds.has(String('offer_' + o.id)))
  ].length;

  // Poll pending offers and incidents for transporteur
  useEffect(() => {
    let timer;
    const load = async () => {
      try {
        if (isTransporteurLike) {
          const [fetchedOffers, fetchedIncidents] = await Promise.all([
            getPendingOffers().catch(() => []),
            getIncidents(selectedTransporteurId).catch(() => [])
          ]);
          const validOffers = Array.isArray(fetchedOffers) ? fetchedOffers : [];
          const activeIncidents = Array.isArray(fetchedIncidents) 
            ? fetchedIncidents.filter(i => i.status !== 'RESOLVED' && i.status !== 'RESOLUE')
            : [];
          
          setNotifications({ offers: validOffers, incidents: activeIncidents });
          setOfferCount(validOffers.length + activeIncidents.length);
        } else {
          setOfferCount(0);
          setNotifications({ offers: [], incidents: [] });
        }
      } catch (_) {
        setOfferCount(0);
        setNotifications({ offers: [], incidents: [] });
      }
    };
    load();
    timer = setInterval(load, 15000);
    return () => { if (timer) clearInterval(timer); };
  }, [isTransporteurLike, selectedTransporteurId]);

  // Real-time WebSocket Notifications
  useWebSocket({
    topic: userId ? `/topic/notifications.${userId}` : null,
    accessToken: token,
    onMessage: (message) => {
      if (message.type === 'SUCCESS') {
        toast.success(message.message, { autoClose: 5000 });
      } else if (message.type === 'ERROR') {
        toast.error(message.message, { autoClose: 10000 });
      } else {
        toast.info(message.message);
      }
    }
  });

  // Auto-expand sidebar menu based on current path
  useEffect(() => {
    const activeItem = navigationItems.find(item =>
      item.isDropdown && item.subItems.some(sub => sub.path === location.pathname)
    );
    if (activeItem) {
      setOpenSubmenus(prev => {
        if (!prev.includes(activeItem.label)) {
          return [...prev, activeItem.label];
        }
        return prev;
      });
    }
  }, [location.pathname]); // removed navigationItems dependency to avoid loop if it's recreated

  return (
    <div className="app-layout">
      {/* Header - Top Level, Full Width */}
      {!hideSidebar && (
        <header className="layout-navbar with-shadow">
          <div className="navbar-logo-container px-3">
            <Logo variant="sidebar" maxWidth="10rem" />
          </div>
          <div className="header-content flex-grow-1">
            <button className="btn btn-link d-md-none me-2" onClick={handleSidebarToggle}>
              <i className="bi bi-list" style={{ fontSize: '2rem' }}></i>
            </button>
            <h1 className="page-title mb-0"></h1>
            <div className="header-actions">
              {userType === 'transporteur' && (
                <div className="dropdown position-relative me-2">
                  <button
                    className="btn btn-link position-relative dropdown-toggle-hide-arrow"
                    title={t('common.notifications')}
                    onClick={() => setNotificationsDropdownOpen(!notificationsDropdownOpen)}
                    onBlur={(e) => {
                      if (!e.currentTarget.parentNode.contains(e.relatedTarget)) {
                        setNotificationsDropdownOpen(false);
                      }
                    }}
                  >
                    <i className="bi bi-bell" style={{ fontSize: '1.7rem' }}></i>
                    {unreadCount > 0 && (
                      <span className="position-absolute badge rounded-pill bg-danger" style={{ top: '0.125rem', right: '0.125rem', fontSize: '0.65rem', padding: '0.35em 0.5em' }}>
                        {unreadCount}
                      </span>
                    )}
                  </button>
                  {notificationsDropdownOpen && (
                    <div
                      className="dropdown-menu dropdown-menu-end show notifications-dropdown"
                      style={{ padding: 0, marginTop: '10px', right: 0 }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {/* Header */}
                      <div className="notif-header">
                        <h6 className="mb-0 fw-bold notif-title">
                          <i className="bi bi-bell-fill me-2"></i>Notifications
                          {unreadCount > 0 && <span className="notif-unread-badge ms-2">{unreadCount} non lues</span>}
                        </h6>
                        {unreadCount > 0 && (
                          <button className="btn btn-sm notif-markall-btn" onClick={markAllRead}>
                            Tout marquer comme lu
                          </button>
                        )}
                      </div>

                      {/* Body */}
                      <div className="notifications-body">
                        {offerCount === 0 && (
                          <div className="notif-empty">
                            <i className="bi bi-bell-slash"></i>
                            <span>Aucune notification</span>
                          </div>
                        )}

                        {/* Checklists (Anciennement CHECK_VEHICULE) */}
                        {notifications.incidents.filter(inc => inc.category === 'CHECKLIST' || inc.category === 'CHECK_VEHICULE').length > 0 && (
                          <div className="notification-group">
                            <div className="notif-section-label text-success">📋 Contrôles & Checklists</div>
                            {notifications.incidents.filter(inc => inc.category === 'CHECKLIST' || inc.category === 'CHECK_VEHICULE').map(inc => {
                              const isRead = readIds.has(String(inc.id));
                              return (
                                <div
                                  key={inc.id}
                                  className={`notif-item ${isRead ? 'notif-item--read' : 'notif-item--unread'}`}
                                  onClick={() => {
                                    markAsRead(inc.id);
                                    setNotificationsDropdownOpen(false);
                                    navigate(`/agent-center?incident=${encodeURIComponent(inc.missionId)}&category=${encodeURIComponent(inc.category)}&chauffeurId=${encodeURIComponent(inc.chauffeurId || '')}`, { state: { triggerAI: true } });
                                  }}
                                >
                                  <div className="notif-item-row">
                                    <div className="notif-item-icon inspection">
                                      <i className="bi bi-clipboard-check-fill"></i>
                                    </div>
                                    <div className="notif-item-content">
                                      <div className="notif-item-title">
                                        Contrôle Véhicule
                                        {!isRead && <span className="notif-dot"></span>}
                                      </div>
                                      <div className="notif-item-route">
                                        <i className="bi bi-geo-alt-fill me-1"></i>
                                        {inc.missionDepartureLocation || '—'} → {inc.missionArrivalLocation || '—'}
                                      </div>
                                      <div className="notif-item-footer">
                                        <span className="notif-action-hint">Voir le rapport <i className="bi bi-chevron-right"></i></span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Incidents Réels */}
                        {notifications.incidents.filter(inc => inc.category !== 'CHECKLIST' && inc.category !== 'CHECK_VEHICULE').length > 0 && (
                          <div className="notification-group">
                            <div className="notif-section-label text-danger">⚠ Incidents en cours</div>
                            {notifications.incidents.filter(inc => inc.category !== 'CHECKLIST' && inc.category !== 'CHECK_VEHICULE').map(inc => {
                              const isRead = readIds.has(String(inc.id));
                              return (
                                <div
                                  key={inc.id}
                                  className={`notif-item ${isRead ? 'notif-item--read' : 'notif-item--unread'}`}
                                  onClick={() => {
                                    markAsRead(inc.id);
                                    setNotificationsDropdownOpen(false);
                                    navigate(`/agent-center?incident=${encodeURIComponent(inc.missionId)}&category=${encodeURIComponent(inc.category)}&chauffeurId=${encodeURIComponent(inc.chauffeurId || '')}`, { state: { triggerAI: true } });
                                  }}
                                >
                                  <div className="notif-item-row">
                                    <div className="notif-item-icon incident">
                                      <i className="bi bi-exclamation-triangle-fill"></i>
                                    </div>
                                    <div className="notif-item-content">
                                      <div className="notif-item-title">
                                        {`Incident — ${inc.category || 'Général'}`}
                                        {!isRead && <span className="notif-dot"></span>}
                                      </div>
                                      <div className="notif-item-route">
                                        <i className="bi bi-geo-alt-fill me-1"></i>
                                        {inc.missionDepartureLocation || '—'} → {inc.missionArrivalLocation || '—'}
                                      </div>
                                      <div className="notif-item-footer">
                                        <span className="notif-action-hint">Gérer l'incident <i className="bi bi-chevron-right"></i></span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Offers */}
                        {notifications.offers.length > 0 && (
                          <div className="notification-group">
                            <div className="notif-section-label text-primary">📦 Offres en attente</div>
                            {notifications.offers.map(o => {
                              const offerId = 'offer_' + o.id;
                              const isRead = readIds.has(offerId);
                              return (
                                <div
                                  key={o.id}
                                  className={`notif-item ${isRead ? 'notif-item--read' : 'notif-item--unread'}`}
                                  onClick={async () => {
                                    markAsRead(offerId);
                                    await saveOffer(o.id);
                                    try { window.dispatchEvent(new CustomEvent('saved-offers-updated')); } catch (_) { }
                                    setNotificationsDropdownOpen(false);
                                    navigate('/client');
                                  }}
                                >
                                  <div className="notif-item-row">
                                    <div className="notif-item-icon offer">
                                      <i className="bi bi-truck"></i>
                                    </div>
                                    <div className="notif-item-content">
                                      <div className="notif-item-title">
                                        Nouvelle offre de mission
                                        {!isRead && <span className="notif-dot"></span>}
                                      </div>
                                      <div className="notif-item-route">
                                        <i className="bi bi-geo-alt me-1"></i>
                                        {o.departureLocation} → {o.arrivalLocation}
                                      </div>
                                      <div className="notif-item-footer">
                                        <span className="badge notif-cargo-badge">{o.cargoType}</span>
                                        <span className="notif-action-hint">Voir et Accepter <i className="bi bi-chevron-right"></i></span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Footer */}
                      <div className="notif-footer">
                        <button className="btn btn-link btn-sm" onClick={() => setNotificationsDropdownOpen(false)}>Fermer</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {isTransporteurLike && (
                <button className="btn btn-link" title="Mission Timeline" onClick={() => navigate('/gantt-chart')} style={{ marginRight: '0.5rem' }}>
                  <i className="bi bi-calendar-range" style={{ fontSize: '1.7rem' }}></i>
                </button>
              )}
              {/* Chatbot button hidden from navbar
              {userType !== 'admin' && (
                <button className="btn btn-link chatbot-btn" title={t('common.chatbot')} onClick={() => setChatBotOpen(true)}>
                  <i className="bi bi-robot" style={{ fontSize: '1.7rem' }}></i>
                </button>
              )}
              */}
              {isResponsable && (
                <div className="transporteur-filter-wrapper me-3 d-none d-md-block">
                  <div className={`dropdown entity-filter-dropdown ${filterDropdownOpen ? 'show' : ''}`}>
                    <button 
                      className="btn btn-filter-premium dropdown-toggle" 
                      type="button" 
                      onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
                      title={t('common.filterByEntity')}
                    >
                      <i className="bi bi-buildings-fill me-2"></i>
                      <span>
                        {selectedTransporteurId 
                          ? (() => {
                              const sub = subcontractors.find(s => s.id === selectedTransporteurId);
                              return sub ? `${sub.firstname} ${sub.lastname}` : t('common.filterByEntity');
                            })()
                          : t('common.allEntities')
                        }
                      </span>
                    </button>
                    <ul className={`dropdown-menu dropdown-menu-end shadow-sm ${filterDropdownOpen ? 'show' : ''}`}>
                      <li>
                        <button 
                          className={`dropdown-item ${!selectedTransporteurId ? 'active' : ''}`}
                          onClick={() => { setSelectedTransporteurId(null); setFilterDropdownOpen(false); }}
                        >
                          <i className="bi bi-grid-fill me-2"></i>
                          {t('common.allEntities')}
                        </button>
                      </li>
                      <li><hr className="dropdown-divider" /></li>
                      {subcontractors.map(sub => (
                        <li key={sub.id}>
                          <button 
                            className={`dropdown-item ${selectedTransporteurId === sub.id ? 'active' : ''}`}
                            onClick={() => { setSelectedTransporteurId(sub.id); setFilterDropdownOpen(false); }}
                          >
                            <i className="bi bi-person-badge me-2"></i>
                            {sub.firstname} {sub.lastname} <span className="text-muted small ms-1">({sub.enterpriseName})</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              <div className="dropdown me-2">
                <button className="btn btn-link dropdown-toggle" type="button" id="langMenu" data-bs-toggle="dropdown">
                  {locale === 'fr' ? 'FR' : 'EN'}
                </button>
                <ul className="dropdown-menu dropdown-menu-end">
                  <li><button className="dropdown-item" onClick={() => setLocale('en')}>EN - English</button></li>
                  <li><button className="dropdown-item" onClick={() => setLocale('fr')}>FR - Français</button></li>
                </ul>
              </div>
              <div className="dropdown">
                <button className="btn btn-link dropdown-toggle" type="button" onClick={handleDropdownToggle}>
                  <i className="bi bi-person-circle"></i>
                </button>
                <ul className={`dropdown-menu dropdown-menu-end${dropdownOpen ? ' show' : ''}`}>
                  <li><NavLink className="dropdown-item" to="/profile" onClick={handleDropdownClose}><i className="bi bi-person me-2"></i>{t('common.profile')}</NavLink></li>
                  <li><button className="dropdown-item" onClick={() => { handleDropdownClose(); handleLogout(); }}><i className="bi bi-box-arrow-right me-2"></i>{t('common.logout')}</button></li>
                </ul>
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Main Structure Container */}
      <div className={`dashboard-container ${hideSidebar ? 'full-screen' : ''}`}>
        {!hideSidebar && (
          <aside className={`sidebar ${isSidebarCollapsed ? 'collapsed' : ''} ${sidebarOpen ? 'open' : ''}`}>
            <nav className="sidebar-nav">
              <ul className="nav flex-column">
                {filteredNavItems.map((item) => (
                  <li key={item.path || item.label} className="nav-item">
                    {item.isDropdown ? (
                      <>
                        <button className={`nav-link dropdown-toggle ${openSubmenus.includes(item.label) ? 'active' : ''}`} onClick={() => handleSubmenuToggle(item.label)}>
                          <i className={item.icon}></i>
                          {!isSidebarCollapsed && <span>{item.label}</span>}
                        </button>
                        {openSubmenus.includes(item.label) && !isSidebarCollapsed && (
                          <ul className="nav flex-column ms-3">
                            {item.subItems.map((subItem) => (
                              <li key={subItem.path} className="nav-item">
                                <NavLink to={subItem.path} className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`} onClick={handleNavClick}>
                                  <i className={subItem.icon}></i>
                                  <span>{subItem.label}</span>
                                </NavLink>
                              </li>
                            ))}
                          </ul>
                        )}
                      </>
                    ) : (
                      <NavLink to={item.path} className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`} end onClick={handleNavClick}>
                        <i className={item.icon}></i>
                        {!isSidebarCollapsed && <span>{item.label}</span>}
                      </NavLink>
                    )}
                  </li>
                ))}
              </ul>
            </nav>
            <div className="sidebar-footer">
              <button className="btn btn-link" onClick={handleLogout}>
                <i className="bi bi-box-arrow-right"></i>
                {!isSidebarCollapsed && <span>{t('common.logout')}</span>}
              </button>
            </div>
          </aside>
        )}
        {sidebarOpen && <div className="sidebar-overlay" onClick={handleOverlayClick}></div>}

        <main className="main-content">
          <div className="content-wrapper">
            {children}
          </div>
        </main>
      </div>

      {/* Overlays/Modals */}
      {chatBotOpen && (
        <div className="chatbot-modal-overlay" onClick={() => setChatBotOpen(false)}>
          <div className="chatbot-modal" onClick={e => e.stopPropagation()}>
            <ChatBotGemini onClose={() => setChatBotOpen(false)} />
          </div>
        </div>
      )}

    </div>
  );
};

export default DashboardLayout;